import { Button } from 'blaze-ui/Button'
import Styles from './PostPreviewBody.module.scss'
import cx from 'classnames'
import SmileyHappyPlus from 'blaze-ui/icons/16/SmileyHappyPlus'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import Picker from '@emoji-mart/react'
import { AlmanacFile, BaseEmoji } from 'types'
import React, { useEffect, useRef } from 'react'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { ArticlePostPreviewActionTypes, maxFiles, useArticlePostPreviewContext } from '../ArticlePostPreviewContext'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { Edit1 } from 'blaze-ui/icons'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { Can } from 'components/Can'
import { useAuthedArticle } from '../hooks'
import { useCreateMediaObjectsWithoutPresigning } from 'hooks/queries/mediaObjects/useCreateMediaObjects'
import { usePostProperties } from '../usePostProperties'
import { AssigneeTypeFromAssetType } from 'hooks/useAlmanacFileSubscription'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { SocialMediaDocTour } from 'components/ProductTour/Tours/SocialMediaDocTour'

interface PreviewInputProps {
  value: string
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>, shouldPersist?: boolean) => void
  maxCharacterCount?: number
  readOnly?: boolean
  isHovered?: boolean
}

export const PreviewInput = ({ value, onChange, maxCharacterCount, readOnly, isHovered }: PreviewInputProps) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const { isDesktop } = useResponsiveDesign()
  const { mutateAsync: createMediaObjects } = useCreateMediaObjectsWithoutPresigning()

  const {
    state: { integration, files },
    almanacFile,
    scheduledPost,
    dispatch,
  } = useArticlePostPreviewContext()

  const assigneeType = AssigneeTypeFromAssetType[almanacFile.asset_type]
  const { status } = usePostProperties({ id: almanacFile.id, type: assigneeType })

  const onUploadSuccess = async (newFiles: AlmanacFile[]) => {
    const maxFilesCount = integration ? maxFiles[integration.provider] : Infinity
    const mediaObjects = await createMediaObjects(
      newFiles
        .map((file) => ({ url: file.url as string, mime_type: file.mime_type }))
        .slice(0, maxFilesCount - files.length),
    )

    dispatch({
      type: ArticlePostPreviewActionTypes.SET_FILES,
      payload: [...files, ...mediaObjects],
    })
  }
  const { uploadImageFiles } = useImportMediaFiles(false, (files) => {
    showToastMessage('All files were uploaded successfully!', 'success')
    onUploadSuccess(files)
  })
  const workspaceId = useCurrentWorkspaceId()
  const uploadsProjectQuery = useUploadsProject(workspaceId)
  const currentWorkspaceId = useCurrentWorkspaceId()
  const authedArticle = useAuthedArticle(almanacFile)
  const uploadsProjectId = uploadsProjectQuery.data?.project?.id

  const handleEmojiSelect = (emoji: string) => {
    if (textAreaRef.current) {
      const textArea = textAreaRef.current
      const start = textArea.selectionStart
      const end = textArea.selectionEnd
      const newValue = value.slice(0, start) + emoji + value.slice(end)
      const scrollTop = textArea.scrollTop

      onChange({ target: { value: newValue } } as React.ChangeEvent<HTMLTextAreaElement>)

      setTimeout(() => {
        textArea.focus()
        textArea.setSelectionRange(start + emoji.length, start + emoji.length)
        textArea.scrollTop = scrollTop
      }, 0)
    }
  }

  const onPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (e.clipboardData.getData('text')) return

    e.preventDefault()
    e.stopPropagation()

    let blob

    for (const item of e.clipboardData.items) {
      if (item.kind === 'file') {
        blob = item.getAsFile()
      }
    }

    if (blob) {
      uploadImageFiles([blob], uploadsProjectId)
    }
  }

  useEffect(() => {
    if (!readOnly && textAreaRef.current && textAreaRef.current !== document.activeElement) {
      textAreaRef.current.focus()
    }
  }, [readOnly])

  return (
    <div className={cx(Styles.textAreaContainer, readOnly && Styles.readOnly)}>
      <Can I="edit" a={authedArticle} inWorkspace={currentWorkspaceId}>
        <SocialMediaDocTour eligible={!readOnly} />
      </Can>
      <div className={Styles.editButtonContainer}>
        {readOnly && isHovered && !scheduledPost?.is_posting && status.value !== PostStatusPropertyValue.Posted && (
          <Can I="edit" a={authedArticle} inWorkspace={currentWorkspaceId}>
            <Button
              frontIcon={Edit1}
              key="edit"
              variant="primary"
              size="sm"
              onPress={() => dispatch({ type: ArticlePostPreviewActionTypes.SET_EDITING, payload: true })}
            >
              Edit Post Content
            </Button>
          </Can>
        )}
      </div>
      <textarea
        className={cx(Styles.previewTextArea, !readOnly && Styles.active)}
        value={value}
        onChange={(e) => {
          // Autosave won't be triggered if content is empty, so we trigger it manually
          const shouldPersistChange = !e.target.value
          onChange(e, shouldPersistChange)
        }}
        onPaste={onPaste}
        rows={6}
        onDoubleClick={() => {
          if (readOnly) {
            dispatch({ type: ArticlePostPreviewActionTypes.SET_EDITING, payload: true })
          }
        }}
        readOnly={readOnly}
        ref={textAreaRef}
      />
      {!readOnly && (
        <div className={Styles.textAreaFooter}>
          {isDesktop && (
            <DropdownTrigger popoverClassName={Styles.popoverOverride}>
              <Button size="xs" variant="tertiary" frontIcon={SmileyHappyPlus}>
                Add Emoji
              </Button>
              {(close) => (
                <Picker
                  autoFocus
                  theme="light"
                  previewPosition="none"
                  onEmojiSelect={async (emoji: BaseEmoji) => {
                    handleEmojiSelect(emoji.native)
                    close()
                  }}
                />
              )}
            </DropdownTrigger>
          )}
          {maxCharacterCount && (
            <div className={Styles.footerRight}>
              Character Count{' '}
              <span
                className={cx({
                  [Styles.exceededLimit]: [...value].length > maxCharacterCount,
                })}
              >
                {[...value].length}
              </span>
              /{maxCharacterCount}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
